/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { analyticsPageType } from '@/constants/analytics/analyticsPageType';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import type { ShelfPart } from '@/types/legacy';
import { useState } from 'react';
import useLatestCallback from 'use-latest-callback';
import { DealDrawer } from '../components/DealDrawer/DealDrawer';
import { useQueryClient } from '@tanstack/react-query';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';

export const useDealDrawer = () => {
  const router = useLegacyRouter();
  const [currentAnalyticsPageType, setCurrentAnalyticsPageType] = useState<string>(
    analyticsPageType.catalogShelf,
  );
  const [dealPart, setDealPart] = useState<ShelfPart | null>(null);
  const [dealSkuPrice, setDealSkuPrice] = useState('');
  const [showBundleDealsDrawer, setShowBundleDealsDrawer] = useState(false);
  const [showYmme, setShowYmme] = useState(false);
  const [closeRef, setCloseRef] = useState<React.RefObject<HTMLButtonElement | null> | null>(null);
  const [dealSkuQuantity, setDealSkuQuantity] = useState(0);
  const [dealFulfillmentTypeId, setDealFulfillmentTypeId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const preferredVehicle = usePreferredVehicle();
  const closeDealsDrawer = useLatestCallback(() => {
    setShowBundleDealsDrawer(false);
    setShowYmme(false);
    if (closeRef?.current) {
      closeRef.current.focus();
    }
  });

  const handleDealClick = useLatestCallback(
    (
      part: ShelfPart,
      price: string,
      quantity: number,
      ref: React.RefObject<HTMLButtonElement | null>,
      fulfillmentTypeId: number | null,
    ) => {
      const isVehicleSpecific = part.recordType === 'app';
      if (!preferredVehicle && isVehicleSpecific) {
        setShowYmme(true);
      }
      setDealPart(part);
      setDealSkuPrice(price);
      setDealSkuQuantity(quantity);
      setDealFulfillmentTypeId(fulfillmentTypeId);
      setCurrentAnalyticsPageType(window.utag_data?.pageType || analyticsPageType.catalogShelf);
      setShowBundleDealsDrawer(true);
      setCloseRef(ref);
    },
  );

  const handleDealsAddToCart = () => {
    void queryClient.invalidateQueries({ queryKey: ['productSkuDetails'] });
  };

  const renderDealDrawer = () => (
    <>
      {!!dealPart && (
        <DealDrawer
          setShowYmme={setShowYmme}
          showYmme={showYmme}
          analyticsPageType={currentAnalyticsPageType}
          open={showBundleDealsDrawer}
          skuId={dealPart.skuNumber.toString()}
          skuRetailPrice={dealSkuPrice}
          skuFulfillmentTypeId={dealFulfillmentTypeId}
          skuQuantity={dealSkuQuantity || 1}
          skuIsVehicleSpecific={dealPart.recordType === 'app'}
          closeDealsDrawer={closeDealsDrawer}
          handleDealsAddToCart={handleDealsAddToCart}
          brand={dealPart.brand}
          partTypeId={dealPart.originalPartTypeId}
          productRepositoryId={dealPart.skuPricingAndAvailability?.productId}
          dealConfigurableLabel={dealPart.skuPricingAndAvailability?.dealConfigurableLabel}
          fittingPartsURL={
            router.route === '/searchresult' ? dealPart.productSeoUrl : router.asPath
          }
          partGroupId={dealPart.partGroupId}
          partTerminologyId={dealPart.partTerminologyId}
        />
      )}
    </>
  );

  return {
    handleDealClick,
    renderDealDrawer,
  };
};
